import { Component, computed, signal, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/service/auth/auth.service';
import { PermissionService } from '../../../core/service/permission/permission.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  section?: string;
  permission?: { project: string; module: string; action: string };
}

const USER_MANAGEMENT_ITEMS: NavItem[] = [
  {
    label: 'Utilisateurs Club', route: '/federation/utilisateurs/club', icon: 'group',
    section: 'Gestion des utilisateurs',
    permission: { project: 'main', module: 'users_club', action: 'read' },
  },
  {
    label: 'Utilisateurs Fédération', route: '/federation/utilisateurs/federation', icon: 'admin_panel_settings',
    section: 'Gestion des utilisateurs',
    permission: { project: 'main', module: 'users_federation', action: 'read' },
  },
];

const NAV_BY_ROLE: Record<string, NavItem[]> = {
  FED_ADMIN: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    ...USER_MANAGEMENT_ITEMS,
  ],
  SUPER_ADMIN: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    ...USER_MANAGEMENT_ITEMS,
  ],
  FED_REFER: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Mes matchs',       route: '/federation/mes-matchs',    icon: 'sports_soccer' },
    { label: 'Feuilles de match',route: '/federation/feuilles',      icon: 'description' },
  ],
  FED_DELEG: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Mes matchs',       route: '/federation/mes-matchs',    icon: 'sports_soccer' },
    { label: 'Rapports',         route: '/federation/rapports',      icon: 'summarize' },
  ],
  CLUB_MANAGER: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
    { label: 'Mes équipes',      route: '/club/equipes',             icon: 'group' },
    { label: 'Calendrier',       route: '/club/calendrier',          icon: 'calendar_month' },
    { label: 'Résultats',        route: '/club/resultats',           icon: 'scoreboard' },
  ],
  CLUB_READONLY: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
    { label: 'Calendrier',       route: '/club/calendrier',          icon: 'calendar_month' },
    { label: 'Résultats',        route: '/club/resultats',           icon: 'scoreboard' },
  ],
};

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
  // Tablet (768-1023px): start on the icon rail so the 256px menu does not eat half the screen.
  isOpen = signal(typeof window === 'undefined' || window.innerWidth < 768 || window.innerWidth >= 1024);
  mobileOpen = signal(false);
  currentUrl = signal<string>('');

  private destroy$ = new Subject<void>();

  navItems = computed<NavItem[]>(() => {
    const roles = this.authService.getRoles();
    // Flat multi-role model — union every held role's nav items, deduped by route, instead of
    // picking a single role's list. Falls back to FED_ADMIN's list if no role matches.
    const byRoute = new Map<string, NavItem>();
    const matchedLists = roles.map(r => NAV_BY_ROLE[r]).filter(Boolean);
    const lists = matchedLists.length ? matchedLists : [NAV_BY_ROLE['FED_ADMIN']];
    for (const items of lists) {
      for (const item of items) {
        if (!byRoute.has(item.route)) byRoute.set(item.route, item);
      }
    }
    return [...byRoute.values()].filter(item =>
      !item.permission || this.permService.can(item.permission.project, item.permission.module, item.permission.action)
    );
  });

  platformLabel = computed<string>(() => {
    const roles = this.authService.getRoles();
    if (roles.includes('FED_REFER')) return 'Arbitre';
    if (roles.includes('FED_DELEG')) return 'Délégué';
    if (this.authService.isClub()) return 'Club';
    return 'Fédération';
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private permService: PermissionService,
  ) {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => this.currentUrl.set(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void { this.isOpen.update(v => !v); }
  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    // The phone drawer always shows labels, never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) this.isOpen.set(true);
  }
  closeMobile(): void { this.mobileOpen.set(false); }

  previousSection(items: NavItem[], index: number): string | undefined {
    return index > 0 ? items[index - 1].section : undefined;
  }

  isActive(route: string): boolean {
    return this.currentUrl().startsWith(route);
  }

  navigate(route: string): void {
    this.closeMobile();
    this.router.navigate([route]);
  }
}
